/**
 * Centralized logging utility for SwiftCarry Admin UI
 *
 * Usage:
 *   import logger from '../utils/logger';
 *
 *   logger.info('User logged in', { userId: 123 });
 *   logger.error('API call failed', error);
 *   logger.warn('Deprecated feature used', { feature: 'oldAPI' });
 *   logger.debug('Debug info', { data: someData });
 */

class Logger {
  constructor() {
    this.isDevelopment = process.env.NODE_ENV === 'development';
    this.isProduction = process.env.NODE_ENV === 'production';
  }

  /**
   * Log informational messages
   * @param {string} message - The message to log
   * @param {Object} data - Additional data to log
   */
  info(message, data = null) {
    if (this.isDevelopment) {
      if (data) {
        // console.log(`ℹ️ INFO: ${message}`, data);
      } else {
        // console.log(`ℹ️ INFO: ${message}`);
      }
    }

    // In production, send to logging service (e.g., Sentry, LogRocket)
    if (this.isProduction) {
      this.sendToLogService('info', message, data);
    }
  }

  /**
   * Log error messages
   * @param {string} message - The error message
   * @param {Error|Object} error - The error object
   */
  error(message, error = null) {
    if (this.isDevelopment) {
      if (error) {

        console.error(`❌ ERROR: ${message}`, error);
      } else {

        console.error(`❌ ERROR: ${message}`);
      }
    }

    // Always log errors to service, even in production
    if (this.isProduction) {
      this.sendToLogService('error', message, error);
    }
  }

  /**
   * Log warning messages
   * @param {string} message - The warning message
   * @param {Object} data - Additional data to log
   */
  warn(message, data = null) {
    if (this.isDevelopment) {
      if (data) {

        console.warn(`⚠️ WARNING: ${message}`, data);
      } else {

        console.warn(`⚠️ WARNING: ${message}`);
      }
    }

    if (this.isProduction) {
      this.sendToLogService('warn', message, data);
    }
  }

  /**
   * Log debug messages (only in development)
   * @param {string} message - The debug message
   * @param {Object} data - Additional data to log
   */
  debug(message, data = null) {
    if (this.isDevelopment) {
      if (data) {

        console.debug(`🐛 DEBUG: ${message}`, data);
      } else {

        console.debug(`🐛 DEBUG: ${message}`);
      }
    }
    // Debug logs are never sent to production logging service
  }

  /**
   * Log table data (useful for debugging arrays of objects)
   * @param {Array} data - Array of objects to display as table
   */
  table(data) {
    if (this.isDevelopment && Array.isArray(data)) {

      console.table(data);
    }
  }

  /**
   * Start a timer for performance monitoring
   * @param {string} label - Label for the timer
   */
  time(label) {
    if (this.isDevelopment) {

      console.time(label);
    }
  }

  /**
   * End a timer for performance monitoring
   * @param {string} label - Label for the timer
   */
  timeEnd(label) {
    if (this.isDevelopment) {

      console.timeEnd(label);
    }
  }

  /**
   * Send logs to external logging service (Sentry, LogRocket, etc.)
   * @private
   * @param {string} level - Log level (info, warn, error)
   * @param {string} message - The message to log
   * @param {Object} data - Additional data
   */
  sendToLogService(level, message, data) {
    // TODO: Implement integration with logging service
    // Example: Sentry.captureMessage(message, { level, extra: data });

    // For now, we'll just use console in production with minimal info
    if (level === 'error') {

      console.error(message, data);
    }
  }
}

// Export singleton instance
const logger = new Logger();
export default logger;
